import { useSearchParams, Link } from 'react-router-dom'
import { Loader2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import Logo from '@/components/Logo'
import AuthBackgroundShape from '@/components/AuthBackgroundShape'
import RegisterForm from '@/components/shadcn-studio/blocks/register-01/register-form'
import { useInvitationDetails } from '@/data/hooks/api'

const AcceptInvitationPage = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const { data: invitationDetails, isLoading: loading, error: queryError } = useInvitationDetails(token)

  const error = queryError ? (queryError as { message?: string })?.message || 'Failed to load invitation details' : null

  if (loading) {
    return (
      <div className='relative flex h-auto min-h-screen items-center justify-center overflow-x-hidden px-4 py-10 sm:px-6 lg:px-8'>
        <div className='absolute'>
          <AuthBackgroundShape />
        </div>

        <Card className='z-1 w-full border-none shadow-md sm:max-w-lg'>
          <CardContent className='flex items-center justify-center py-12'>
            <div className='flex flex-col items-center gap-3 text-center'>
              <Loader2 className='h-8 w-8 animate-spin text-primary' />
              <p className='text-sm text-muted-foreground'>Loading invitation details...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error || !invitationDetails) {
    return (
      <div className='relative flex h-auto min-h-screen items-center justify-center overflow-x-hidden px-4 py-10 sm:px-6 lg:px-8'>
        <div className='absolute'>
          <AuthBackgroundShape />
        </div>

        <Card className='z-1 w-full border-none shadow-md sm:max-w-lg'>
          <CardHeader className='gap-6'>
            <Logo className='gap-3' showText={false} />

            <div className='flex flex-col items-center text-center'>
              <div className='mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10'>
                <XCircle className='h-8 w-8 text-destructive' />
              </div>
              <CardTitle className='mb-1.5 text-2xl'>Invalid Invitation</CardTitle>
              <CardDescription className='text-base'>
                {error || 'This invitation link is invalid or has expired.'}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className='space-y-4'>
            <Button className='w-full' asChild>
              <Link to='/auth/login'>Go to Login</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className='relative flex h-auto min-h-screen items-center justify-center overflow-x-hidden px-4 py-10 sm:px-6 lg:px-8'>
      <div className='absolute'>
        <AuthBackgroundShape />
      </div>

      <Card className='z-1 w-full border-none shadow-md sm:max-w-lg'>
        <CardHeader className='gap-3 items-center justify-center'>
          <Logo className='gap-3 mx-auto' showText={false} />

          <div className='text-center'>
            <CardTitle className='mb-1.5 text-2xl text-center'>Accept Team Invitation</CardTitle>
            <CardDescription className='text-base'>
              You've been invited to join {invitationDetails.organization_name}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          <RegisterForm invitationToken={token || undefined} invitationEmail={invitationDetails?.email} />

          <p className='text-muted-foreground text-center mt-4'>
            Already have an account?{' '}
            <Link to='/auth/login' className='text-card-foreground hover:underline'>
              Sign in instead
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

export default AcceptInvitationPage
